import { createdByUserToSearchText, formatDateTime, includesSearchTerm } from '@/shared/utils/helpers';
import { getTaskTypeLabel } from '@/shared/domain/taskTypes';
import { matchesTaskTypeFilter } from '@/features/tasks/utils/taskTypeFilter';
export const getTrainingTaskDisplayName = (usecase) => {
    if (!usecase) return 'Task name';
    return (usecase.sub_group_name && usecase.sub_group_name.trim()) || usecase.usecase_name || 'Task name';
};

/**
 * Composed tasks: the Models list row carries the umbrella label on `usecase_name`
 * (from `sub_group_name`) and the per-subtask key on `objective_name` (from the
 * LLM usecase's `usecase_name`). When only one subtask has trainings, the grouped
 * table stays flat — use this to still show which subtask a version belongs to.
 */
const norm = (s) => String(s ?? '').trim();

export function shouldShowTrainingSubObjective(version, umbrellaDisplayName) {
    const objective = norm(version?.objective_name);
    const umbrella = norm(umbrellaDisplayName ?? version?.usecase_name);
    if (!objective) return false;
    if (!umbrella) return true;
    return objective.localeCompare(umbrella, undefined, { sensitivity: 'base' }) !== 0;
}

const MODEL_DRAWER_COMPLETED_ALIASES = new Set([
    'completed',
    'ready to deploy',
    'ready_to_deploy',
    'deployed',
]);

/**
 * Status values that should enable Download / Deploy in ModelDetailsDrawer.
 * Maps usecase lifecycle labels (e.g. "Ready to Deploy") to training "completed".
 */
export function normalizeModelDrawerTrainingStatus(status) {
    if (status == null || status === '') return undefined;
    const s = String(status).toLowerCase();
    if (MODEL_DRAWER_COMPLETED_ALIASES.has(s)) return 'completed';
    return String(status);
}

/**
 * Resolve training status for drawer header actions from version, API details, or context.
 */
export function resolveModelDrawerStatusText(version, trainingFromDetails) {
    const training = trainingFromDetails || {};
    const raw =
        version?.status ??
        training.status ??
        (training.finished_datetime ? 'completed' : null);
    const normalized = normalizeModelDrawerTrainingStatus(raw);
    return normalized ?? '';
}

/**
 * Build the `version` prop for ModelDetailsDrawer when opened from Evaluation.
 * Mirrors deployment fields set on Models-page training rows.
 */
export function buildModelDrawerVersionFromEvaluation({
    localUsecase,
    currentUsecaseId,
    selectedVersion,
    distillationEventId,
    evaluationResult,
    trainStatuses = {},
    evaluationResultsVisible = false,
    defaultClientId,
}) {
    const ver = selectedVersion;
    const uc = localUsecase;
    const base = {
        llm_usecase_id: currentUsecaseId,
        version: ver,
        client_id: uc?.client_id || defaultClientId,
        distillation_event_id: distillationEventId,
    };
    if (!uc || !currentUsecaseId) return base;

    const depVer = uc.deployed_version;
    const storageDeploy =
        typeof localStorage !== 'undefined'
            ? localStorage.getItem(`deploying_${currentUsecaseId}`)
            : null;
    const storageUndeploy =
        typeof localStorage !== 'undefined'
            ? localStorage.getItem(`undeploying_${currentUsecaseId}`)
            : null;
    const sd =
        storageDeploy != null && storageDeploy !== ''
            ? parseFloat(storageDeploy, 10)
            : NaN;
    const su =
        storageUndeploy != null && storageUndeploy !== ''
            ? parseFloat(storageUndeploy, 10)
            : NaN;
    const deploying_version =
        Number.isFinite(sd) && ver != null && sd === ver ? ver : undefined;
    const vUndeploy = depVer ?? ver;
    const undeploying_version =
        Number.isFinite(su) && vUndeploy != null && su === vUndeploy ? vUndeploy : undefined;

    const wsStatus = distillationEventId
        ? trainStatuses[distillationEventId]?.status
        : null;
    const status =
        normalizeModelDrawerTrainingStatus(wsStatus)
        || normalizeModelDrawerTrainingStatus(evaluationResult?.distillation_status)
        || normalizeModelDrawerTrainingStatus(uc.status)
        || (evaluationResultsVisible ? 'completed' : undefined);

    return {
        ...base,
        status,
        task_type: uc.task_type,
        usecase_name: uc.usecase_name,
        objective_name: uc.usecase_name,
        deployed_version: depVer,
        is_deployed: uc.is_deployed,
        usecase_status: uc.status,
        deploying_version,
        undeploying_version,
        isThisVersionDeployed: Boolean(
            uc.is_deployed && depVer != null && ver != null && Number(depVer) === Number(ver),
        ),
        hasAnyDeployedVersion: Boolean(uc.is_deployed && depVer != null),
    };
}

/**
 * True when the Models table should use the composite layout: umbrella task row
 * plus per-sub-task sections (each with its own description).
 */
export function isCompositeUsecaseGroup(usecaseName, subTasks = []) {
    const umbrella = norm(usecaseName);
    if (subTasks.length > 1) return true;
    return subTasks.some((subTask) =>
        shouldShowTrainingSubObjective(
            { objective_name: subTask.sub_task_name, usecase_name: umbrella },
            umbrella,
        ),
    );
}

/** Drawer / list title: umbrella, optional subtask, then version. */
export function formatTrainingModelDetailsTitle({ umbrellaName, objectiveName, version }) {
    const v = version != null ? version : 1;
    const umbrella = norm(umbrellaName);
    const objective = norm(objectiveName);
    const showSub =
        objective &&
        umbrella &&
        objective.localeCompare(umbrella, undefined, { sensitivity: 'base' }) !== 0;
    if (showSub) {
        return `${umbrella} · ${objective} · v${v}.0`;
    }
    if (umbrella) {
        return `${umbrella}-v${v}.0`;
    }
    if (objective) {
        return `${objective}-v${v}.0`;
    }
    return `v${v}.0`;
}

/**
 * Groups training data by project/group name for the grouped table view
 * @param {Array} trainings - Array of training objects
 * @returns {Array} Grouped training data with three-level hierarchy: Project → Task → Version
 */
export const groupTrainingsByProject = (trainings) => {
    const grouped = {};

    trainings.forEach(training => {
        const groupName = training.group_name || 'Unnamed Project';
        const usecaseName = training.usecase_name || 'Task name';
        const subTaskName = training.objective_name || usecaseName;
        const subTaskKey = training.llm_usecase_id || subTaskName;

        if (!grouped[groupName]) {
            grouped[groupName] = {
                group_name: groupName,
                project_goal: training.project_goal || '',
                usecases: {}
            };
        }

        if (!grouped[groupName].usecases[usecaseName]) {
            grouped[groupName].usecases[usecaseName] = {
                usecase_name: usecaseName,
                usecase_goal: '',
                versions: [],
                sub_tasks: {}
            };
        }

        if (!grouped[groupName].usecases[usecaseName].sub_tasks[subTaskKey]) {
            grouped[groupName].usecases[usecaseName].sub_tasks[subTaskKey] = {
                sub_task_key: subTaskKey,
                sub_task_name: subTaskName,
                usecase_goal: training.usecase_goal || '',
                llm_usecase_id: training.llm_usecase_id,
                versions: []
            };
        }

        // Add training as a version
        grouped[groupName].usecases[usecaseName].versions.push(training);
        grouped[groupName].usecases[usecaseName].sub_tasks[subTaskKey].versions.push(training);
    });

    const sortVersions = (versions) => versions.sort((a, b) => {
        // Sort by version number if available, otherwise by creation date
        const versionA = a.version || 1;
        const versionB = b.version || 1;
        return versionB - versionA; // Newest first
    });

    // Convert to array and sort by group name
    const result = Object.values(grouped).map(project => ({
        ...project,
        usecases: Object.values(project.usecases).map(usecase => {
            const subTasks = Object.values(usecase.sub_tasks)
                .map(subTask => ({
                    ...subTask,
                    versions: sortVersions(subTask.versions)
                }))
                .sort((a, b) => a.sub_task_name.localeCompare(b.sub_task_name));

            const hasMultipleSubTasks = isCompositeUsecaseGroup(usecase.usecase_name, subTasks);
            const soleSubTaskGoal = subTasks.length === 1 ? subTasks[0].usecase_goal : '';
            return {
                ...usecase,
                // Umbrella row has no description when sub-tasks carry their own goals.
                usecase_goal: hasMultipleSubTasks ? '' : soleSubTaskGoal,
                versions: sortVersions(usecase.versions),
                sub_tasks: subTasks,
                has_multiple_sub_tasks: hasMultipleSubTasks,
            };
        }).sort((a, b) => a.usecase_name.localeCompare(b.usecase_name))
    }));

    return result.sort((a, b) => a.group_name.localeCompare(b.group_name));
};

/**
 * Maps training status to filter keys
 * @param {string} status - Training status
 * @returns {string} Filter key
 */
export const mapStatusToFilterKey = (status) => {
    if (!status) return 'unknown';

    const statusLower = status.toLowerCase();

    if (statusLower === 'completed' || statusLower === 'ready to deploy') {
        return 'completed';
    }
    if (statusLower === 'failed') {
        return 'failed';
    }
    if (statusLower === 'waiting') {
        return 'waiting';
    }

    // All other statuses (including running, waiting, ready, failed, etc.) are considered "running" for filtering
    // This includes: running, training, in training, waiting, ready, ready to train, failed, paused, etc.
    return 'running';
};

/**
 * Filters training data based on search, project, status, user, and task type filter criteria
 * @param {Array} trainings - Array of training objects
 * @param {string} searchTerm - Search term for filtering across visible metadata
 * @param {string} projectFilter - Project filter value
 * @param {Array} statusFilter - Array of status filter keys
 * @param {string|Array} userFilter - User filter value(s). Can be "All Users" or array of user IDs
 * @param {string|string[]} taskTypeFilter - Task type filter. "All", a TaskType, classification subtype (`ClassificationResponse:single` / `:multi`), or an array of those values
 * @returns {Array} Filtered training data
 */
export const filterTrainings = (trainings, searchTerm = '', projectFilter = 'All Projects', statusFilter = [], userFilter = 'All Users', taskTypeFilter = 'All') => {
    let filtered = trainings;

    // Filter by project
    if (projectFilter && projectFilter !== 'All Projects') {
        filtered = filtered.filter(training => training.group_name === projectFilter);
    }

    // Filter by task type(s)
    if (taskTypeFilter && taskTypeFilter !== 'All') {
        filtered = filtered.filter(training => matchesTaskTypeFilter(training, taskTypeFilter));
    }

    // Filter by status
    if (statusFilter && statusFilter.length > 0) {
        filtered = filtered.filter(training => {
            const statusKey = mapStatusToFilterKey(training.status);
            return statusFilter.includes(statusKey);
        });
    }

    // Filter by user(s)
    if (userFilter && userFilter !== 'All Users') {
        const userIds = Array.isArray(userFilter) ? userFilter : [userFilter];
        filtered = filtered.filter(training => {
            const userId = training.created_by_user?.sub || training.created_by_user?.email;
            return userIds.includes(userId);
        });
    }

    // Filter by search term
    if (searchTerm) {
        filtered = filtered.filter(training => {
            const taskTypeLabel = getTaskTypeLabel(training.task_type, training.is_multilabel, training.classify_speakers);
            return includesSearchTerm(
                searchTerm,
                training.usecase_name,
                training.group_name,
                training.status,
                training.task_type,
                taskTypeLabel,
                training.objective_name,
                training.new_model_name,
                training.base_model_name,
                training.version ? `V${training.version}.0` : '',
                createdByUserToSearchText(training.created_by_user),
                training.created_datetime ? formatDateTime(training.created_datetime) : '',
                training.finished_datetime ? formatDateTime(training.finished_datetime) : '',
            );
        });
    }

    return filtered;
};

/**
 * Gets unique project names for the filter dropdown
 * @param {Array} trainings - Array of training objects
 * @returns {Array} Array of unique project names
 */
export const getUniqueProjectNames = (trainings) => {
    const projectNames = [...new Set(trainings.map(training => training.group_name).filter(Boolean))];
    return projectNames.sort();
};

/**
 * Gets unique users who created trainings for the filter dropdown
 * @param {Array} trainings - Array of training objects
 * @returns {Array} Array of unique user objects with id and name
 */
export const getUniqueUsers = (trainings) => {
    const usersMap = new Map();
    trainings.forEach(training => {
        if (training.created_by_user) {
            const userId = training.created_by_user.sub || training.created_by_user.email;
            const userName = training.created_by_user.name || training.created_by_user.email || training.created_by_user.sub || 'Unknown';
            if (userId && !usersMap.has(userId)) {
                usersMap.set(userId, {
                    id: userId,
                    name: userName,
                    user: training.created_by_user
                });
            }
        }
    });
    return Array.from(usersMap.values()).sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Formats training data for display
 * @param {Object} training - Training object
 * @returns {Object} Formatted training object
 */
export const formatTrainingForDisplay = (training) => {
    return {
        ...training,
        displayName: training.usecase_name || 'Task name',
        displayVersion: training.version ? `V${training.version}.0` : 'V1.0',
        displayStatus: training.status || 'unknown',
        displayProgress: training.progress || 0
    };
};

/**
 * Gets the count of usecases for a project
 * @param {Object} project - Project object with usecases
 * @returns {number} Number of usecases
 */
export const getUsecaseCount = (project) => {
    return project.usecases ? project.usecases.length : 0;
};

/**
 * Gets the count of versions for a usecase
 * @param {Object} usecase - Usecase object with versions
 * @returns {number} Number of versions
 */
export const getVersionCount = (usecase) => {
    return usecase.versions ? usecase.versions.length : 0;
}; 